export const isDev = process.env.NODE_ENV === 'development';
export const isProd = process.env.NODE_ENV === 'production';

/**
 * A base URL has paths appended to it, so a trailing slash would double up —
 * `host//api/v1/...`. Harmless against the current gateway, but it depends on
 * how a deployment happens to set the variable, and some gateways route `//`
 * differently. Normalised here so no caller has to think about it.
 */
export const withoutTrailingSlash = (value: string): string => value.replace(/\/+$/, '');

/**
 * What an environment gets when it asks for nothing — the set that was hard-coded
 * in App.tsx, so a deployment that says nothing keeps the behaviour it had.
 */
export const DEFAULT_OKTA_SCOPES = [
	'openid',
	'profile',
	'offline_access',
	'ps-authapi-read',
	'ps-authapi-write',
	'ps-auth-ui',
	'ps-traveler-api-dev',
];

/**
 * Scopes arrive as a single string. OAuth writes them space-separated and .env
 * files conventionally use commas, so both are accepted rather than making
 * whoever writes the file remember which one this parser wanted.
 */
export const parseScopes = (value: string | undefined): string[] => {
	const scopes = (value ?? '').split(/[\s,]+/).filter(Boolean);

	return scopes.length > 0 ? scopes : DEFAULT_OKTA_SCOPES;
};

/**
 * Client env vars (inlined at build time by dotenv-webpack). Each is referenced
 * literally below so the bundler can statically replace it. All are required
 * except the scopes, which fall back to DEFAULT_OKTA_SCOPES.
 */
export const env = {
	oktaClientId: process.env.PUBLIC_AUTH_OKTA_ID ?? '',
	oktaIssuer: process.env.PUBLIC_AUTH_OKTA_ISSUER ?? '',
	oktaScopes: parseScopes(process.env.PUBLIC_AUTH_OKTA_SCOPES),
	authServicesBaseUrl: withoutTrailingSlash(process.env.AUTH_SERVICES_BASE_URL ?? ''),
};

/**
 * Fails fast at startup when a required env var is missing, so a deploy
 * misconfiguration surfaces as a clear error instead of an opaque Okta failure
 * or an "undefined/..." API URL. Call once from the app entrypoint.
 */
export const validateEnv = (): void => {
	const missing = Object.entries({
		PUBLIC_AUTH_OKTA_ID: process.env.PUBLIC_AUTH_OKTA_ID,
		PUBLIC_AUTH_OKTA_ISSUER: process.env.PUBLIC_AUTH_OKTA_ISSUER,
		AUTH_SERVICES_BASE_URL: process.env.AUTH_SERVICES_BASE_URL,
	})
		.filter(([, value]) => !value)
		.map(([key]) => key);

	if (missing.length > 0) {
		throw new Error(
			`Missing required environment variables: ${missing.join(', ')}. Check your .env file.`
		);
	}
};
